// features/findings/predictions-registry.js
// ════════════════════════════════════════════════════════════════════
// PREDICTIONS REGISTRY (Phase F9)
//
// Aggregates pre-registered predictions from every finding into a
// single sortable, filterable list for the /lab/predictions calendar.
//
// Each prediction is annotated with the parent finding's metadata
// (title, citationHandle, path, domain, version) so the calendar
// can link back without a second lookup.
//
// Status conventions used across all findings:
//   open      — pending resolution
//   held      — resolved, prediction confirmed
//   overturned— resolved, prediction wrong
//   partial   — resolved, mixed evidence
//   retired   — withdrawn before resolution (rare; document why)
// ════════════════════════════════════════════════════════════════════

import {
  PREDICTIONS as ASYMMETRIC_PREDICTIONS,
  FINDING_META as ASYMMETRIC_META,
} from './asymmetric-underdog-inversion';

import {
  KH_PREDICTIONS,
  FINDING_META as KH_META,
} from './korea-hungary-divergence';

import {
  LOCKIN_PREDICTIONS,
  FINDING_META as LOCKIN_META,
} from './korea-lockin';

import {
  PCS_PREDICTIONS,
  FINDING_META as PCS_META,
} from './positive-case-search';

import {
  HAREDI_PREDICTIONS,
  FINDING_META as HAREDI_META,
} from './israel-haredi-floor';

import {
  REFUGIA_PREDICTIONS,
  FINDING_META as REFUGIA_META,
} from './refugia-template';

import {
  ECO_PREDICTIONS,
  FINDING_META as ECO_META,
} from './substrate-symmetric-refugia';

import {
  BOOM_PREDICTIONS,
  FINDING_META as BOOM_META,
} from './baby-boom-detective';

import {
  QUEBEC_PREDICTIONS,
  FINDING_META as QUEBEC_META,
} from './quebec-quiet-revolution';

import {
  CONSTRAINT_PREDICTIONS,
  FINDING_META as CONSTRAINT_META,
} from './constraint-maneuver';

function annotate(predictions, meta, idPrefix) {
  return predictions.map((p, i) => ({
    ...p,
    id: `${idPrefix}-${i + 1}`,
    parentFinding: {
      title: meta.title,
      shortTitle: meta.shortTitle,
      citationHandle: meta.citationHandle,
      path: `/findings/${meta.domain}/${meta.year}/${meta.slug}`,
      domain: meta.domain,
      formName: meta.formName,
      version: meta.version,
    },
    sortKey: extractSortKey(p.resolutionDate),
  }));
}

/**
 * Best-effort parse of varied resolutionDate formats:
 *   '2027-01'                        → '2027-01-31'
 *   '2028 KSH release'               → '2028-12-31'
 *   '2030+ multi-country cohort data'→ '2030-12-31'
 *   '2026 retrospective'             → '2026-12-31'
 *   'case-specific (rolling)'        → '9999-12-31' (sorts last)
 */
function extractSortKey(label) {
  if (!label) return '9999-12-31';
  const monthMatch = label.match(/(\d{4})-(\d{2})/);
  if (monthMatch) return `${monthMatch[1]}-${monthMatch[2]}-31`;
  const yearMatch = label.match(/(\d{4})/);
  if (yearMatch) return `${yearMatch[1]}-12-31`;
  return '9999-12-31';
}

export const PREDICTIONS_REGISTRY = [
  ...annotate(ASYMMETRIC_PREDICTIONS, ASYMMETRIC_META, 'asymmetric'),
  ...annotate(KH_PREDICTIONS, KH_META, 'korea-hungary'),
  ...annotate(LOCKIN_PREDICTIONS, LOCKIN_META, 'korea-lockin'),
  ...annotate(PCS_PREDICTIONS, PCS_META, 'positive-case-search'),
  ...annotate(HAREDI_PREDICTIONS, HAREDI_META, 'israel-haredi-floor'),
  ...annotate(REFUGIA_PREDICTIONS, REFUGIA_META, 'refugia-template'),
  ...annotate(ECO_PREDICTIONS, ECO_META, 'substrate-symmetric-refugia'),
  ...annotate(BOOM_PREDICTIONS, BOOM_META, 'baby-boom-detective'),
  ...annotate(QUEBEC_PREDICTIONS, QUEBEC_META, 'quebec-quiet-revolution'),
  ...annotate(CONSTRAINT_PREDICTIONS, CONSTRAINT_META, 'constraint-maneuver'),
];

export function listPredictions({ status, domain } = {}) {
  let list = [...PREDICTIONS_REGISTRY];
  if (status) list = list.filter((p) => p.status === status);
  if (domain) list = list.filter((p) => p.parentFinding.domain === domain);
  list.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return list;
}

export function predictionStats() {
  const all = PREDICTIONS_REGISTRY;
  return {
    total: all.length,
    open: all.filter((p) => p.status === 'open').length,
    held: all.filter((p) => p.status === 'held').length,
    overturned: all.filter((p) => p.status === 'overturned').length,
    partial: all.filter((p) => p.status === 'partial').length,
  };
}

/** Days until resolution; null if non-parseable or in past. */
export function daysUntil(sortKey, today = new Date()) {
  if (!sortKey || sortKey === '9999-12-31') return null;
  const target = new Date(sortKey + 'T00:00:00Z');
  if (Number.isNaN(target.getTime())) return null;
  const ms = target.getTime() - today.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
